import { useQueryClient } from '@tanstack/react-query'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { useTranslation } from 'react-i18next'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import {
    createRevenueTarget,
    updateRevenueTarget,
    deleteRevenueTarget,
    getRevenueTarget,
    getRevenueTargets,
    getRevenueTargetsWithActuals,
    getMonthlyRevenue,
    getDashboardAnalytics,
    getDashboardData,
} from '../services'
import {
    CreateRevenueTargetPayload,
    UpdateRevenueTargetPayload,
    GetRevenueTargetsParams,
    GetMonthlyRevenueParams,
    GetRevenueWithActualsParams,
    GetDashboardParams,
} from '../types'

// Query Keys
export const REVENUE_QUERY_KEYS = {
    targets: QUERY_KEYS.DASHBOARD_REVENUE_TARGETS,
    target: (id: string) => [QUERY_KEYS.DASHBOARD_REVENUE_TARGETS, id] as const,
    targetsWithActuals: QUERY_KEYS.DASHBOARD_REVENUE_TARGETS_WITH_ACTUALS,
    monthlyRevenue: QUERY_KEYS.DASHBOARD_MONTHLY_REVENUE,
    dashboardAnalytics: QUERY_KEYS.DASHBOARD_ANALYTICS,
    dashboardData: QUERY_KEYS.DASHBOARD_DATA,
} as const

// Get Revenue Targets
export const useRevenueTargets = (params: GetRevenueTargetsParams = {}) => {
    return useQueryWithHandler({
        queryKey: [REVENUE_QUERY_KEYS.targets, params],
        queryFn: () => getRevenueTargets(params),
        staleTime: 5 * 60 * 1000, // 5 minutes
    })
}

// Get Single Revenue Target
export const useRevenueTarget = (id: string) => {
    return useQueryWithHandler({
        queryKey: REVENUE_QUERY_KEYS.target(id),
        queryFn: () => getRevenueTarget(id),
        enabled: !!id,
        staleTime: 5 * 60 * 1000,
    })
}

// Get Dashboard Data (Direct endpoint)
export const useDashboardData = (params: GetDashboardParams = {}) => {
    return useQueryWithHandler({
        queryKey: [REVENUE_QUERY_KEYS.dashboardData, params],
        queryFn: () => getDashboardData(params),
        staleTime: 2 * 60 * 1000, // 2 minutes for more frequent updates
    })
}

// Get Revenue Targets with Actuals (for charts)
export const useRevenueTargetsWithActuals = (
    params: GetRevenueWithActualsParams = {}
) => {
    return useQueryWithHandler({
        queryKey: [
            REVENUE_QUERY_KEYS.targetsWithActuals,
            JSON.stringify(params),
        ],
        queryFn: () => getRevenueTargetsWithActuals(params),
        staleTime: 2 * 60 * 1000, // 2 minutes for more frequent updates
    })
}

// Get Monthly Revenue
export const useMonthlyRevenue = (params: GetMonthlyRevenueParams) => {
    return useQueryWithHandler({
        queryKey: [REVENUE_QUERY_KEYS.monthlyRevenue, params],
        queryFn: () => getMonthlyRevenue(params),
        enabled: !!params.year,
        staleTime: 5 * 60 * 1000,
    })
}

// Get Dashboard Analytics
export const useDashboardAnalytics = (
    params: GetRevenueWithActualsParams = {}
) => {
    return useQueryWithHandler({
        queryKey: [REVENUE_QUERY_KEYS.dashboardAnalytics, params],
        queryFn: () => getDashboardAnalytics(params),
        staleTime: 2 * 60 * 1000,
    })
}

// Create Revenue Target Mutation
export const useCreateRevenueTarget = () => {
    const queryClient = useQueryClient()
    const { t } = useTranslation('dashboard')

    return useMutationWithHandler(
        {
            mutationFn: (payload: CreateRevenueTargetPayload) =>
                createRevenueTarget(payload),
        },
        {
            reactQueryKeys: QUERY_KEYS.GENERIC,
            successMessage: t('target_revenue.create_success'),
            errorMessage: t('target_revenue.create_error'),
            onSuccess: () => {
                queryClient.invalidateQueries({
                    queryKey: [REVENUE_QUERY_KEYS.targets],
                })
                queryClient.invalidateQueries({
                    queryKey: [REVENUE_QUERY_KEYS.targetsWithActuals],
                })
                queryClient.invalidateQueries({
                    queryKey: [REVENUE_QUERY_KEYS.dashboardAnalytics],
                })
                queryClient.invalidateQueries({
                    queryKey: [REVENUE_QUERY_KEYS.dashboardData],
                })
            },
        }
    )
}

// Update Revenue Target Mutation
export const useUpdateRevenueTarget = () => {
    const queryClient = useQueryClient()
    const { t } = useTranslation('dashboard')

    return useMutationWithHandler(
        {
            mutationFn: (payload: UpdateRevenueTargetPayload) =>
                updateRevenueTarget(payload),
        },
        {
            reactQueryKeys: QUERY_KEYS.GENERIC,
            successMessage: t('target_revenue.update_success'),
            errorMessage: t('target_revenue.update_error'),
            onSuccess: (_, variables) => {
                queryClient.invalidateQueries({
                    queryKey: [REVENUE_QUERY_KEYS.targets],
                })
                queryClient.invalidateQueries({
                    queryKey: REVENUE_QUERY_KEYS.target(variables.id),
                })
                queryClient.invalidateQueries({
                    queryKey: [REVENUE_QUERY_KEYS.targetsWithActuals],
                })
                queryClient.invalidateQueries({
                    queryKey: [REVENUE_QUERY_KEYS.dashboardAnalytics],
                })
                queryClient.invalidateQueries({
                    queryKey: [REVENUE_QUERY_KEYS.dashboardData],
                })
            },
        }
    )
}

// Delete Revenue Target Mutation
export const useDeleteRevenueTarget = () => {
    const queryClient = useQueryClient()
    const { t } = useTranslation('dashboard')

    return useMutationWithHandler(
        {
            mutationFn: (id: string) => deleteRevenueTarget(id),
        },
        {
            reactQueryKeys: QUERY_KEYS.GENERIC,
            successMessage: t('target_revenue.delete_success'),
            errorMessage: t('target_revenue.delete_error'),
            onSuccess: () => {
                queryClient.invalidateQueries({
                    queryKey: [REVENUE_QUERY_KEYS.targets],
                })
                queryClient.invalidateQueries({
                    queryKey: [REVENUE_QUERY_KEYS.targetsWithActuals],
                })
                queryClient.invalidateQueries({
                    queryKey: [REVENUE_QUERY_KEYS.dashboardAnalytics],
                })
                queryClient.invalidateQueries({
                    queryKey: [REVENUE_QUERY_KEYS.dashboardData],
                })
            },
        }
    )
}
